import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Check,
  Schedule,
  Visibility,
  Link as LinkIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface SharePropertyDialogProps {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  propertyTitle: string;
}

const SharePropertyDialog: React.FC<SharePropertyDialogProps> = ({
  open,
  onClose,
  propertyId,
  propertyTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');
  const [propertyImages, setPropertyImages] = useState<string[]>([]);
  const [selectedImageIndexes, setSelectedImageIndexes] = useState<number[]>([]);

  const fetchPropertyImages = useCallback(async () => {
    try {
      setImagesLoading(true);
      const response = await api.get(`/properties/${propertyId}`);
      const images: string[] = response.data.images || [];
      setPropertyImages(images);
      setSelectedImageIndexes(images.map((_, i) => i));
    } catch {
      // silently fall through — no images
    } finally {
      setImagesLoading(false);
    }
  }, [propertyId]);

  const toggleImageSelection = (index: number) => {
    setSelectedImageIndexes((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index];
    });
    setShareLink('');
  };

  const generateShareLink = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const body: any = { propertyId };
      if (propertyImages.length > 0) {
        const selectedImages = selectedImageIndexes.map((i) => propertyImages[i]);
        body.images = selectedImages;
        body.selectedImageIndexes = selectedImageIndexes;
      }

      const response = await api.post('/share-links', body);

      const { token, expiresAt: expiration } = response.data.shareLink;
      const fullLink = `${window.location.origin}/shared/property/${token}`;

      setShareLink(fullLink);
      setExpiresAt(expiration);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  }, [propertyId, propertyImages, selectedImageIndexes]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleClose = () => {
    setShareLink('');
    setExpiresAt('');
    setCopied(false);
    setError('');
    setPropertyImages([]);
    setSelectedImageIndexes([]);
    onClose();
  };

  const formatExpirationTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    if (open) {
      fetchPropertyImages();
    }
  }, [open, fetchPropertyImages]);

  const isGenerateDisabled = loading || imagesLoading || (propertyImages.length > 0 && selectedImageIndexes.length === 0);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          background: 'linear-gradient(135deg, #1967d2 0%, #1557b0 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon />
          <Typography variant="h6" fontWeight={600}>
            Share Property
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
            Property:
          </Typography>
          <Typography variant="body1" fontWeight={600} color="text.primary">
            {propertyTitle}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Image selection */}
        {imagesLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading images...</Typography>
          </Box>
        ) : propertyImages.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ImageIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Select Images to Include ({selectedImageIndexes.length}/{propertyImages.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {propertyImages.map((url, idx) => {
                const selected = selectedImageIndexes.includes(idx);
                return (
                  <Box
                    key={idx}
                    onClick={() => toggleImageSelection(idx)}
                    sx={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selected ? '2px solid #1967d2' : '2px solid transparent',
                      opacity: selected ? 1 : 0.5,
                      transition: 'all 0.2s',
                      '&:hover': { opacity: 0.85 },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={`Image ${idx + 1}`}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Checkbox
                      checked={selected}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 0.3,
                        color: 'white',
                        '&.Mui-checked': { color: '#1967d2' },
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        borderRadius: '0 0 0 4px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleImageSelection(idx)}
                    />
                  </Box>
                );
              })}
            </Box>
            {selectedImageIndexes.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>
                Select at least one image to generate a link.
              </Alert>
            )}
          </Box>
        ) : null}

        {/* Share link display */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generating secure link...
            </Typography>
          </Box>
        ) : shareLink ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                icon={<Schedule sx={{ fontSize: '1.1rem' }} />}
                label={`Expires in ${formatExpirationTime(expiresAt)}`}
                size="small"
                color="warning"
                sx={{ fontWeight: 600, '& .MuiChip-icon': { color: 'inherit' } }}
              />
            </Box>

            <Alert
              severity="info"
              icon={<Visibility />}
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: '#e3f2fd',
                '& .MuiAlert-icon': { color: '#1976d2' },
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                This link will automatically expire in 2 hours. Recipients can view
                property details without logging in.
              </Typography>
            </Alert>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                backgroundColor: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <TextField
                fullWidth
                value={shareLink}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                  },
                }}
              />
              <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                <Button
                  variant="contained"
                  onClick={handleCopyLink}
                  startIcon={copied ? <Check /> : <ContentCopy />}
                  sx={{
                    minWidth: 120,
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: copied ? '#4caf50' : '#1967d2',
                    boxShadow: '0 2px 8px rgba(25, 103, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: copied ? '#45a049' : '#1557b0',
                      boxShadow: '0 4px 12px rgba(25, 103, 210, 0.4)',
                    },
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Tooltip>
            </Box>

            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: '#fff3e0',
                border: '1px solid #ffb74d',
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#e65100', mb: 0.5 }}>
                ⚠️ Security Note:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Anyone with this link can view the property details. The link
                will expire automatically after 2 hours. You can generate a new
                link anytime.
              </Typography>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#f8f9fa' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e0e0e0',
            color: '#666',
            '&:hover': { borderColor: '#bdbdbd', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          Close
        </Button>
        <Button
          onClick={generateShareLink}
          variant="outlined"
          disabled={isGenerateDisabled}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#1967d2',
            borderColor: '#1967d2',
            '&:hover': { borderColor: '#1557b0', backgroundColor: 'rgba(25, 103, 210, 0.08)' },
          }}
        >
          {shareLink ? 'Generate New Link' : 'Generate Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePropertyDialog;
